import { grey } from '@mui/material/colors';
import is from '@sindresorhus/is';
import type { FunctionComponent, ReactNode } from 'react';
import { useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import { seniorityLevelSchema } from '~/shared/data-contract/seniority-level';

import { DataCell } from '../../../../../design/components/data-grid/DataGridContainer';
import { relocationPreferenceMap } from '../../../../common/enums-translation-mappers';
import { useOneUserQuery } from '../../../../query-hooks/users';
import { SidebarCard } from './SidebarCard';

type PersonalInformationProps = {
    readonly detailActions?: ReactNode;
    readonly userId: string;
};

type VisiblePersonalInformation = {
    readonly label: string;
    readonly value: string;
};

export const PersonalInformation: FunctionComponent<PersonalInformationProps> = (props) => {
    const { detailActions, userId } = props;
    const { translate } = useContext(ApplicationContext);
    const { userQuery } = useOneUserQuery(userId);
    const { jobSeeking, relocationPreference, seniorityLevel } = userQuery.data ?? {};

    const parsedSeniorityLevel = seniorityLevelSchema.safeParse(seniorityLevel);
    const seniorityLevelLabel = parsedSeniorityLevel.success
        ? translate(`common.enums.seniority-level.${parsedSeniorityLevel.data}`)
        : String(seniorityLevel ?? 0);
    const hasJobSeekingStatus = (jobSeeking ?? 'not open') !== 'not open';
    const jobSeekingStatus = hasJobSeekingStatus
        ? translate('pages.recruiter-profile.personal-information.job-seeking.open')
        : translate('pages.recruiter-profile.personal-information.job-seeking.not-open');
    const relocationPreferenceLabel = translate(relocationPreferenceMap[relocationPreference ?? 'not open']);

    const visiblePersonalInformationData: readonly VisiblePersonalInformation[] = [
        ...(is.nonEmptyString(jobSeeking)
            ? [
                  {
                      label: translate('pages.recruiter-profile.personal-information.job-seeking.title'),
                      value: jobSeekingStatus,
                  },
              ]
            : []),
        ...(is.nonEmptyString(relocationPreference)
            ? [
                  {
                      label: translate('pages.recruiter-profile.personal-information.location-status'),
                      value: relocationPreferenceLabel,
                  },
              ]
            : []),
        ...(is.number(seniorityLevel)
            ? [
                  {
                      label: translate('pages.recruiter-profile.personal-information.seniority-level'),
                      value: seniorityLevelLabel,
                  },
              ]
            : []),
    ];

    return (
        <SidebarCard
            backgroundColor={grey[100]}
            detailActions={detailActions}
            title={translate('pages.recruiter-profile.personal-information.title')}
        >
            {visiblePersonalInformationData.map(({ label, value }) => (
                <DataCell key={label} label={label} value={value} />
            ))}
        </SidebarCard>
    );
};
